/* eslint-disable operator-linebreak */
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LiliToastDefaultConfig } from '../../../DesignSystem/LiliToast/LiliToast';
import LiliNotification, { LiliNotificationType } from '../../../DesignSystem/LiliNotification/LiliNotification';
import LiliTabs from '../../../DesignSystem/LiliTabs/LiliTabs';
import { Layout } from '../../../components/Layout/Layout';
import { FEATURE_FLAG } from '../../../constants/general';
import Heading from '../../../generic-components/Heading';
import { InAppComponent } from '../../../hooks/query/useGetInappComponentsLogs';
import { useGetSingleInAppComponentLog } from '../../../hooks/utilities/useGetSingleInAppComponentLog';
import { canUpgradeToUseFeature, validateFeatureFlag } from '../../../services/featureFlagService';
import { useStore } from '../../../store';
import { STYLES } from '../consts';
import { useFetchExtrenalTransactions } from '../hooks/useFetchExternalTransactions';
import { useFetchTransactions } from '../hooks/useFetchTransactions';
import { SyncCardStatus, useLinkedAccounts } from '../hooks/useLinkedAccounts';
import { ETransactionsState, useTransactionsData } from '../hooks/useTransactionsData';
import { TransactionTabs } from '../state/createTransactionSlice';
import { TransactionDetailsDrawer } from './TransactionDetailsDrawer';
import { TransactionTable } from './TransactionTable';
import { TransactionFilter } from './filter/TransactionFilter';
import { ExportTable } from './shared/ExportTable';
import { LoadingToast } from './shared/LoadingToast';
import { CategorizeCreditCardWarningModal } from './sync-your-card/CategorizeCreditCardWarningModal';
import SyncYourCardButtonAndModals from './sync-your-card/SyncYourCardButtonAndModals';
import SyncedCardsWrapper from './sync-your-card/SyncedCardsWrapper';
import { featureAccess, LimitedFeatureType } from '../../../services/appAccess.service';
import { FeatureAccess } from '../../AccountSettings/components/rolesAndPermissions/PermissionsList';

const TransactionsTopBar = () => {
  const { selectedTransactionTab } = useStore((state) => state);
  const shouldShowSyncCardFeature =
    validateFeatureFlag(FEATURE_FLAG.SYNC_CARD) || canUpgradeToUseFeature(FEATURE_FLAG.SYNC_CARD);
  const transactionsListPermission = featureAccess(null, LimitedFeatureType.TransactionsListView);

  return (
    <div className='flex items-center justify-between mb-[16px]'>
      <TransactionFilter isCreditCardFilter={selectedTransactionTab === TransactionTabs.LINKED_CREDIT_CARDS} />
      <div className='flex justify-center items-center'>
        {shouldShowSyncCardFeature && transactionsListPermission === FeatureAccess.READ_WRITE && (
          <SyncYourCardButtonAndModals />
        )}
        {validateFeatureFlag(FEATURE_FLAG.EXPORT_TRANSACTION) && <ExportTable />}
      </div>
    </div>
  );
};

export const FlowContext = createContext<any>({});
export const TransactionsWrapper = () => {
  const { t } = useTranslation();
  const { selectedTransactionTab, setSelectedTransactionTab, selectedSyncedCard } = useStore((state) => state);
  const [pollingForExternalTransactions, setPollingForExternalTransactions] = useState<boolean>(false);
  const { customerId } = useStore((state) => state.user);
  const setSelectedSyncedCard = useStore((state) => state.setSelectedSyncedCard);
  const { data: linkedAccounts = [], isLoading: isLinkedAccountsLoading } = useLinkedAccounts((linkedAccountsRes) => {
    if (linkedAccountsRes.length > 0 && !selectedSyncedCard) {
      setSelectedSyncedCard(linkedAccountsRes[0]);
    }
  });
  const { mutateAsync: fetchTransactions } = useFetchTransactions();
  const { mutateAsync: fetchExternalTransactions } = useFetchExtrenalTransactions();
  const selectedTransaction = useStore((state) => state.selectedTransaction);
  const showWarningCategoryModal = useStore((state) => state.showWarningCategoryModal);
  const setShowWarningCategoryModal = useStore((state) => state.setShowWarningCategoryModal);
  const setShowSyncCardModal = useStore((state) => state.setShowSyncCardModal);
  const {
    data: appComponentLog,
    isLoading: isLoadingAppComponent,
    refetch: refetchAppComponentLog,
  } = useGetSingleInAppComponentLog(customerId, InAppComponent.LINKED_CARD_CATEGORIZE_MODAL);
  const shouldShowSyncCardFeature = validateFeatureFlag(FEATURE_FLAG.SYNC_CARD);

  const {
    start,
    reset,
    handleFilterTypeAndAmount,
    state: dataState,
    handleFilterPeriod,
    loadMore,
    localizedString,
    generateQueryString,
  } = useTransactionsData({
    fetchTransactions:
      selectedTransactionTab === TransactionTabs.BANK_TRANSACTIONS ? fetchTransactions : fetchExternalTransactions,
  });
  const resetSlice = useStore((state) => state.resetSlice);
  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const contextValue = {
    start,
    reset,
    handleFilterTypeAndAmount,
    dataState,
    handleFilterPeriod,
    loadMore,
    localizedString,
    generateQueryString,
  };

  const shouldShowSyncCardsWrapper = useMemo(
    () => linkedAccounts.length > 0 || (linkedAccounts.length === 0 && isLinkedAccountsLoading),
    [linkedAccounts, isLinkedAccountsLoading],
  );

  useEffect(() => {
    if (linkedAccounts.length === 0 && !isLinkedAccountsLoading) {
      setSelectedTransactionTab(TransactionTabs.BANK_TRANSACTIONS);
    }
  }, [linkedAccounts, isLinkedAccountsLoading]);

  useEffect(() => {
    resetSlice();
    start();
    return () => {
      reset(true);
      resetSlice();
    };
  }, [selectedTransactionTab, selectedSyncedCard, pollingForExternalTransactions]);

  const transactionTabs = [
    {
      title: t('TRANSACTIONS_FACELIFT.MAIN_WRAPPER.BANK_TRANSACTIONS'),
      content: (
        <div className='mt-[24px]'>
          <TransactionsTopBar />
          {selectedTransaction && <TransactionDetailsDrawer />}
        </div>
      ),
    },
    {
      title: t('TRANSACTIONS_FACELIFT.MAIN_WRAPPER.LINKENKED_CREDIT_CARDS'),
      content: (
        <div className='mt-[24px]'>
          {shouldShowSyncCardsWrapper && (
            <SyncedCardsWrapper syncedCards={linkedAccounts} isLoading={isLinkedAccountsLoading} />
          )}
          <TransactionsTopBar />
          {selectedTransaction && <TransactionDetailsDrawer />}
        </div>
      ),
    },
  ];

  const handleCreditCardWarningModalSyncCard = () => {
    refetchAppComponentLog();
    setShowWarningCategoryModal(false);
    if (linkedAccounts.length === 0) {
      setShowSyncCardModal(true);
    } else if (linkedAccounts.length === 1) {
      setSelectedTransactionTab(TransactionTabs.LINKED_CREDIT_CARDS);
    }
  };

  const onTabIndexChanged = (index: number) => {
    if (index === TransactionTabs.LINKED_CREDIT_CARDS) {
      setSelectedSyncedCard(linkedAccounts[0]);
    }
    setSelectedTransactionTab(index);
  };

  useEffect(() => {
    const currentLinkedCard = linkedAccounts.find((account) => account.externalId === selectedSyncedCard?.externalId);
    if (currentLinkedCard?.syncStatus === SyncCardStatus.PENDING) {
      setPollingForExternalTransactions(true);
      toast(
        <LiliNotification
          type={LiliNotificationType.WARNING}
          text={t('TRANSACTIONS_FACELIFT.SYNC_CARD.SYNCING_CREDIT_CARD')}
          id='sync-credit-card-loading'
          undismissable={false}
        />,
        {
          ...LiliToastDefaultConfig,
          position: toast.POSITION.BOTTOM_CENTER,
          autoClose: false,
        },
      );
    } else {
      setPollingForExternalTransactions(false);
      toast.dismiss();
    }
  }, [linkedAccounts, selectedSyncedCard]);

  const shouldShowCategorizeWarningModal =
    showWarningCategoryModal &&
    shouldShowSyncCardFeature &&
    !(appComponentLog?.doNotShowAgain || false) &&
    !isLoadingAppComponent;

  return (
    <Layout
      shouldHideChatBot
      wrapperClassName='relative w-full h-full flex items-center justify-between gap-[24px] bg-bw01 ml-[100px] pc-md:ml-[256px]'
    >
      <FlowContext.Provider value={contextValue}>
        <div className={STYLES.WRAPPER}>
          <Heading
            testId='pageHeader'
            title={t('TRANSACTIONS_FACELIFT.MAIN_WRAPPER.TITLE')}
            className={STYLES.PAGE_TITLE}
          />
          {linkedAccounts.length > 0 && shouldShowSyncCardFeature ? (
            <div className='pr-[37px]'>
              <LiliTabs
                activeIndex={selectedTransactionTab}
                onTabIndexChanged={onTabIndexChanged}
                tabs={transactionTabs}
              />
            </div>
          ) : (
            <div className='mr-[30px]'>
              <TransactionsTopBar />
              {selectedTransaction && <TransactionDetailsDrawer />}
            </div>
          )}

          <TransactionTable
            shouldLockScroll={dataState === ETransactionsState.LoadingMore}
            isLoading={dataState === ETransactionsState.Loading || pollingForExternalTransactions}
          />
          {dataState === ETransactionsState.LoadingMore && <LoadingToast />}
        </div>
        {shouldShowCategorizeWarningModal && (
          <CategorizeCreditCardWarningModal
            onFinished={handleCreditCardWarningModalSyncCard}
            onCanceled={() => setShowWarningCategoryModal(false)}
            multipleAccounts={linkedAccounts.length > 0}
            showGotIt={linkedAccounts.length > 1}
            transactionDetails={selectedTransaction?.details || ''}
          />
        )}
      </FlowContext.Provider>
    </Layout>
  );
};
